<template>
  <div class="home-care-container">
    <!-- Configuration Card -->
    <div class="config-card">
      <div class="card-header">
        <div class="header-icon">
          <i class="fas fa-home"></i>
        </div>
        <div class="header-content">
          <h3 class="card-title">Configuração Home Care</h3>
          <p class="card-subtitle">
            Configure o acesso a residentes para cuidado domiciliar
          </p>
        </div>
      </div>

      <div class="card-content">
        <div class="form-section">
          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-toggle-on mr-2"></i>
              Ativar configuração Home Care para este usuário
            </label>
            <div class="input-group">
              <div class="select-wrapper">
                <select class="form-select" v-model="allow">
                  <option value="NAO">Não</option>
                  <option value="SIM">Sim</option>
                </select>
                <div class="select-icon">
                  <i class="fas fa-chevron-down"></i>
                </div>
              </div>
              <div class="action-button">
                <v-btn-timeout
                  @click="config"
                  :disabled="loadingConfig"
                  class="btn-config-modern"
                >
                  <i class="fas fa-save mr-2"></i>
                  {{ loadingConfig ? "Salvando..." : "Salvar" }}
                </v-btn-timeout>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Errors Section -->
    <div v-if="errors.length > 0">
      <ErrorsMessages :errors="errors" />
    </div>

    <!-- Residents Management Section -->
    <div v-if="allow_home_care == 1" class="residents-section">
      <!-- Link Residents Modal -->
      <b-modal
        title="VINCULAR RESIDENTE(S)"
        id="link"
        no-close-on-backdrop
        hide-footer
        size="lg"
        modal-class="link-residents-modal"
      >
        <div class="modal-header-custom">
          <div class="modal-icon">
            <i class="fas fa-link"></i>
          </div>
          <div class="modal-text">
            <h3 class="modal-title">Vincular Residentes</h3>
            <p class="modal-subtitle">
              Selecione os residentes para vincular ao usuário
            </p>
          </div>
        </div>

        <div class="modal-content-custom">
          <b-overlay
            :show="loadingResidents"
            opacity="0.99"
            class="loading-overlay"
          >
            <div v-if="residents.length == 0" class="no-results">
              <i class="fas fa-users no-results-icon"></i>
              <h4 class="no-results-title">Nenhum residente disponível</h4>
              <p class="no-results-text">
                Todos os residentes já estão vinculados
              </p>
            </div>

            <div v-if="residents.length > 0" class="residents-content">
              <div class="search-section">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Pesquisar residente..."
                  outlined
                  dense
                  hide-details
                  class="search-input"
                />
              </div>

              <div class="table-section">
                <v-data-table
                  dense
                  :headers="[
                    { text: 'NOME DO RESIDENTE', value: 'name' },
                    {
                      text: 'AÇÕES',
                      value: 'action',
                      sortable: false,
                      align: 'center',
                    },
                  ]"
                  :items="residents"
                  :search="search"
                  class="modern-table"
                  :footer-props="{
                    'items-per-page-options': [10, 25, 100],
                    'items-per-page-text': 'Mostrar',
                  }"
                >
                  <template v-slot:[`item.action`]="{ item }">
                    <v-btn
                      small
                      class="btn-link-modern"
                      :disabled="loadingLink"
                      @click="link(item)"
                    >
                      <i class="fas fa-link mr-2"></i>
                      Vincular
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </div>
          </b-overlay>
        </div>
      </b-modal>

      <!-- Linked Residents Card -->
      <div class="linked-residents-card">
        <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
          <div class="card-header">
            <div class="header-left">
              <div class="header-icon">
                <i class="fas fa-users"></i>
              </div>
              <div class="header-content">
                <h3 class="card-title">Residentes Vinculados</h3>
                <p class="card-subtitle">
                  Gerencie os residentes vinculados para Home Care
                </p>
              </div>
            </div>
            <div class="header-actions">
              <v-btn class="btn-add-resident" @click="$bvModal.show('link')">
                <i class="fas fa-plus mr-2"></i>
                Vincular Residente
              </v-btn>
            </div>
          </div>

          <div class="card-content">
            <div
              v-if="user_has_resident.length == 0"
              class="no-linked-residents"
            >
              <div class="empty-state">
                <i class="fas fa-user-friends empty-icon"></i>
                <h4 class="empty-title">Nenhum residente vinculado</h4>
                <p class="empty-text">
                  Clique no botão "Vincular Residente" para começar
                </p>
              </div>
            </div>

            <div v-if="user_has_resident.length > 0" class="residents-list">
              <div
                v-for="(item, key) in user_has_resident"
                :key="key"
                class="resident-item"
              >
                <div class="resident-info">
                  <div class="resident-avatar">
                    <i class="fas fa-user"></i>
                  </div>
                  <div class="resident-details">
                    <h4 class="resident-name">{{ item.name }}</h4>
                    <span class="resident-label">Residente Home Care</span>
                  </div>
                </div>
                <div class="resident-actions">
                  <ConfirmationDialog
                    :disabled="loadingLink"
                    small
                    open-text="Remover"
                    headerClass="modal-header-i"
                    message-title="Remover Vínculo"
                    message-body="Deseja realmente remover o vínculo deste residente?"
                    open-class="btn-modern btn-modern-delete"
                    open-left-icon="fa-unlink"
                    @yes="unlink(item)"
                  />
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../Components/ErrorsMessages";
import ConfirmationDialog from "../Components/ConfirmationDialog";
import VBtnTimeout from "../Components/VBtnTimeout";
import permission from "../../lib/permission";
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

export default {
  name: "HomeCare",
  components: {
    ErrorsMessages,
    ConfirmationDialog,
    VBtnTimeout,
  },
  props: {
    user_id: { type: [Number, String] },
    allow_home_care: { type: [Number, String, Boolean] },
  },
  data() {
    return {
      search: "",
      loading: false,
      loadingConfig: false,
      loadingLink: false,
      loadingResidents: false,
      allow: "NAO",
      errors: [],
      residents: [],
      user_has_resident: [],
    };
  },
  watch: {
    allow_home_care() {
      if (this.allow_home_care == 1) {
        this.allow = "SIM";
      } else {
        this.allow = "NAO";
      }
    },
  },
  created() {
    this.retrieve();
  },
  methods: {
    permission,
    async retrieve() {
      await this.paginate();
      await this.getResidents();
    },
    async getResidents() {
      try {
        this.loadingResidents = true;
        let { user_has_resident } = this;
        const response = await api.get("/rest-home/resident/active");
        const { error, errors, residents } = response.data;
        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          let allows = [];
          for (let resident of residents) {
            let exist = user_has_resident.find(
              (user_has) => user_has.resident_id == resident.id
            );
            if (!exist) {
              allows.push(resident);
            }
          }
          this.residents = allows;
        }
        this.loadingResidents = false;
      } catch (error) {
        console.log(error);
      }
    },
    async paginate() {
      try {
        this.loading = true;
        let { user_id } = this;
        const params = { user_id, status: "ATIVO" };
        const response = await api.get("/rest-home/user/home-care/residents", {
          params,
        });
        const { error, errors, residents } = response.data;
        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.user_has_resident = residents;
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
    async config() {
      try {
        this.loadingConfig = true;
        let { user_id, allow } = this;
        let allowHomeCare = null;
        if (allow == "SIM") {
          allowHomeCare = 1;
        }
        let params = { user_id, allow_home_care: allowHomeCare };
        const response = await api.post(
          "/rest-home/user/home-care/config",
          params
        );
        const { error, errors, message } = response.data;
        if (!error) {
          this.$emit("save");
          toast(message, "success");
          this.errors = [];
        } else {
          this.errors = errors;
        }
        this.loadingConfig = false;
      } catch (error) {
        console.log(error);
      }
    },
    async link({ id }) {
      try {
        this.loadingLink = true;
        let { user_id } = this;
        let params = { user_id, resident_id: id };
        const response = await api.post(
          "/rest-home/user/home-care/link",
          params
        );
        const { error, errors, message } = response.data;
        if (!error) {
          this.retrieve();
          this.$bvModal.hide("link");
          toast(message, "success");
          this.errors = [];
        } else {
          this.errors = errors;
        }
        this.loadingLink = false;
      } catch (error) {
        console.log(error);
      }
    },
    async unlink({ id }) {
      try {
        this.loadingLink = true;
        let data = { id };
        const response = await api.delete("/rest-home/user/home-care/unlink", {
          data,
        });
        const { error, errors, message } = response.data;
        if (!error) {
          this.retrieve();
          toast(message, "success");
          this.errors = [];
        } else {
          this.errors = errors;
        }
        this.loadingLink = false;
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.home-care-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
}

/* Configuration Card */
.config-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.header-content {
  flex: 1;
}

.card-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.card-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  align-items: center;
}

.card-content {
  padding: 24px;
}

/* Form Section */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
}

.input-group {
  display: flex;
  gap: 12px;
  align-items: stretch;
}

.select-wrapper {
  position: relative;
  flex: 1;
}

.form-select {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  appearance: none;
  transition: all 0.3s ease;
}

.form-select:focus {
  outline: none;
  border-color: #0f65b0;
  background: white;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.select-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
  font-size: 12px;
}

.action-button {
  display: flex;
  align-items: center;
}

.btn-config-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-config-modern:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

/* Residents Section */
.residents-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Linked Residents Card */
.linked-residents-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.btn-add-resident {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-resident:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4) !important;
}

/* No Linked Residents */
.no-linked-residents {
  padding: 60px 24px;
}

.empty-state {
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  color: #d1d5db;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Residents List */
.residents-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}

.resident-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.resident-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

.resident-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.resident-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.resident-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resident-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  text-transform: uppercase;
}

.resident-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-remove-modern {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-remove-modern:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

/* Modal Customizations */
.link-residents-modal >>> .modal-dialog {
  max-width: 700px;
}

.link-residents-modal >>> .modal-content {
  border: none;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.link-residents-modal >>> .modal-header {
  display: none;
}

.link-residents-modal >>> .modal-body {
  padding: 0;
}

.modal-header-custom {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.modal-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.modal-text {
  flex: 1;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
}

.modal-subtitle {
  font-size: 14px;
  margin: 0;
  opacity: 0.9;
}

.modal-content-custom {
  padding: 24px;
}

.loading-overlay {
  border-radius: 16px;
}

/* No Results in Modal */
.no-results {
  text-align: center;
  padding: 40px 24px;
}

.no-results-icon {
  font-size: 48px;
  color: #d1d5db;
  margin-bottom: 16px;
}

.no-results-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.no-results-text {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Residents Content in Modal */
.residents-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.search-section {
  max-width: 300px;
}

.search-input {
  border-radius: 12px;
}

.table-section {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

/* Table Styling */
.modern-table >>> thead tr th {
  background: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.btn-link-modern {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 6px 12px !important;
  font-weight: 600 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-link-modern:hover:not(:disabled) {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .home-care-container {
    gap: 16px;
    padding: 0 16px;
  }

  .card-header {
    padding: 20px;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .header-left {
    align-items: center;
  }

  .card-content {
    padding: 20px;
  }

  .input-group {
    flex-direction: column;
  }

  .residents-list {
    padding: 20px;
    gap: 12px;
  }

  .resident-item {
    padding: 16px;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .resident-info {
    justify-content: center;
    text-align: center;
  }

  .resident-actions {
    text-align: center;
  }

  .modal-header-custom {
    padding: 20px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .modal-content-custom {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .card-header {
    padding: 16px;
  }

  .card-content {
    padding: 16px;
  }

  .header-icon,
  .modal-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .card-title,
  .modal-title {
    font-size: 18px;
  }

  .resident-avatar {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .resident-name {
    font-size: 14px;
  }

  .residents-list {
    padding: 16px;
  }

  .resident-item {
    padding: 12px;
  }
}
</style>